import * as Sentry from '@sentry/vue'
import configs from '@/config'
import { HOST_REQUEST_TTL_MS, buildHostRequest, createHostRequestId } from '@/utils/masterAgentHostRequest'
import { isFeatureEnabled } from '../featureToggles.module'

const FEATURE_TOGGLES_IS_FEATURE_ENABLED = 'featureToggles/isFeatureEnabled'
const FEATURE_TOGGLES_CAN_USE_MASTER_AGENT_EDITOR = 'featureToggles/canUseMasterAgentEditor'

// AHA-47691 convert hand-off: what a pending host request needs but Vuex state must not
// hold (not serialisable): id -> { resolve, timer }. `resolve` settles the promise the
// asking embed is waiting on; `timer` is the request's expiry. An entry exists only
// while its request is pending, so nothing is armed when nothing is pending.
const hostRequestRuntime = new Map()

// Settle the waiter of request `id` (true = acked) and disarm its expiry. Exactly once:
// the entry is removed first, so a second call finds nothing.
const finishHostRequest = (id, acked) => {
  const runtime = hostRequestRuntime.get(id)
  if (!runtime) return
  hostRequestRuntime.delete(id)
  clearTimeout(runtime.timer)
  runtime.resolve(acked)
}

const aiChatbotKeyFlag = (rootState) => {
  if (!rootState.currentUser || rootState.currentUser?.isGuest) return null
  return `ai-chatbot-indicator-seen-${rootState.currentUser.id}`
}

// Persist the docked-panel open state across a same-tab reload but not a
// tab/window close — sessionStorage is exactly that. User-agnostic key because
// this is rehydrated at module-load, before the store knows the current user.
// Rehydrating `true` is safe: GlobalChatbotContainer only renders the panel when
// the bridge is on and hides it in restricted states (isChatBotHidden).
const GLOBAL_PANEL_OPEN_KEY = 'ai-chatbot-global-panel-open'

// Defensive: read-only build entry points have no window, and sessionStorage can
// throw in private-mode/quota situations.
const hasSessionStorage = () => typeof window !== 'undefined' && !!window.sessionStorage

const readGlobalPanelOpen = () => {
  if (!hasSessionStorage()) return false
  try {
    return window.sessionStorage.getItem(GLOBAL_PANEL_OPEN_KEY) === 'true'
  } catch (error) {
    return false
  }
}

const writeGlobalPanelOpen = (value) => {
  if (!hasSessionStorage()) return
  try {
    window.sessionStorage.setItem(GLOBAL_PANEL_OPEN_KEY, value ? 'true' : 'false')
  } catch (error) {
    Sentry.captureException(error)
  }
}

// The FAB assistant iframe URL (aha-chatbot-app). AHA-47691: two routes run side by
// side while the master agent rolls out —
//   /master-agent/  the master agent (deck tools, file upload, Worker /chat/editor)
//   /sandbox/    today's panel, byte-for-byte
// The route is the ONLY thing the flag changes; query string is identical.
const buildAssistantPanelUrl = (presentationId, { masterAgent = false, lang } = {}) => {
  // Same guard every sibling getter carries. Without it an unset VUE_APP_AI_APP_URL
  // yields the TRUTHY string 'undefined/sandbox/', which passes the `v-if="…&& url"`
  // checks downstream and mounts an iframe pointing at the presenter's own 404 page.
  if (!configs.aiAppUrl) return null
  const route = masterAgent ? '/master-agent/' : '/sandbox/'
  const params = { assistantSource: 'master-agent' }
  if (presentationId) params.presentationId = presentationId
  if (lang) params.lang = lang
  const qs = new URLSearchParams(params).toString()
  return qs ? `${configs.aiAppUrl}${route}?${qs}` : `${configs.aiAppUrl}${route}`
}

// The legacy (pre-bridge) Slide-agent iframe URL production shows in the in-editor
// AI tab. Single source of truth shared by aiChatbotUrl's legacy branch and
// slideAgentIframeUrl. v5 users get `/slides-v5/`, else the `/` root.
const buildSlideAgentUrl = ({
  presentationId, userId, userCity, useV5, lang,
}) => {
  // See buildAssistantPanelUrl — 'undefined/?userId=…' is truthy and renders a 404 in the
  // editor's AI tab instead of the tab simply not offering the agent.
  if (!configs.aiAppUrl) return null
  const params = {
    presentationId, userId, userCity, assistantSource: 'slides', lang,
  }
  Object.keys(params).forEach((key) => params[key] == null && delete params[key])
  const path = useV5 ? '/slides-v5/' : '/'
  return `${configs.aiAppUrl}${path}?${new URLSearchParams(params).toString()}`
}

const _state = {
  lastKnownPresentationId: null,
  isAIChatbotIndicatorVisible: false,
  isIframeReady: false,
  // AHA2-146: readiness of the native Slide-agent right-nav iframe (AIAutoPanel),
  // tracked separately from isIframeReady (which reflects GlobalChatbotContainer's
  // docked /sandbox iframe). The import postMessage targets the Slide-agent surface
  // when the tool-bridge flag is ON, so its readiness gate must watch THIS iframe.
  isSlideAgentIframeReady: false,
  // Whether the app-wide docked AI panel is open. Lives in Vuex and
  // GlobalChatbotContainer mounts OUTSIDE <router-view>, so an open panel survives
  // route changes; rehydrated from sessionStorage to also survive a same-tab reload.
  isGlobalPanelOpen: readGlobalPanelOpen(),
  // AHA-47691 slice 3 — last change the master agent made to an embedded app's open item: { kind, id, seq }.
  embedContentChange: null,
  // AHA-47691 convert hand-off — the ONE request an embedded app asked the master agent
  // to act on and the panel has not acked yet: { id, request, queuedAt, openedPanel }.
  // Memory only, deliberately NOT persisted like isGlobalPanelOpen: a reload must never
  // replay it. `openedPanel` = the docked panel was closed when the request was accepted.
  pendingHostRequest: null,
  // When the last host request settled (acked or dropped); drives the embed's cooldown.
  hostRequestSettledAt: null,
}

const _getters = {
  canUseAIChatbot: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_FEATURE_ENABLED]('release-ai-chatbot')
  },
  canUseReportPresentationAgent: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_FEATURE_ENABLED]('AHA-38849-report-presentation-agent')
  },
  canUseMasterReportAgent: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_FEATURE_ENABLED]('release-AHA-39854-master-report-agent')
  },
  canUseSlidesAgentV5: (_, __, ___, rootGetters) => {
    return rootGetters[FEATURE_TOGGLES_IS_FEATURE_ENABLED]('release-AHA-42634-slides-agent-v5')
  },
  // Declared in the Unleash module (featureToggles); re-exposed here so the
  // assistant components keep reading it from the aiChatbot namespace.
  canUsePresenterToolBridge: (_, __, ___, rootGetters) => rootGetters['featureToggles/canUsePresenterToolBridge'],
  // AHA-47691: declared in featureToggles; re-exposed here like the bridge flag.
  canUseMasterAgentEditor: (_, __, ___, rootGetters) => (
    rootGetters[FEATURE_TOGGLES_CAN_USE_MASTER_AGENT_EDITOR] === true
  ),
  // AHA-47691 convert hand-off: declared in featureToggles; re-exposed the same way.
  canUseMasterAgentConvertHandoff: (_, __, ___, rootGetters) => (
    rootGetters['featureToggles/canUseMasterAgentConvertHandoff'] === true
  ),
  // The FAB assistant iframe URL, used when the bridge is on.
  sandboxIframeUrl: (state, __, rootState, rootGetters) => {
    const presentationId = rootState?.presentation?.id || state.lastKnownPresentationId
    return buildAssistantPanelUrl(presentationId, {
      masterAgent: rootGetters?.[FEATURE_TOGGLES_CAN_USE_MASTER_AGENT_EDITOR] === true,
      lang: rootState?.presenterLocale,
    })
  },

  aiChatbotUrl: (state, __, rootState, rootGetters) => {
    // Bridge ON: agent choice lives inside the iframe, so just embed /sandbox/ or
    // /master-agent/ depending on the master-agent variant. Flag OFF => the legacy
    // multi-agent behaviour below runs verbatim (staging's aiAppUrl migration —
    // the flag-off path is whatever production renders today).
    if (rootGetters?.['aiChatbot/canUsePresenterToolBridge']) {
      const presentationId = rootState?.presentation?.id || state.lastKnownPresentationId
      return buildAssistantPanelUrl(presentationId, {
        masterAgent: rootGetters?.[FEATURE_TOGGLES_CAN_USE_MASTER_AGENT_EDITOR] === true,
        lang: rootState?.presenterLocale,
      })
    }
    if (!configs.aiAppUrl) { return null }
    const currentPresentationId = rootState?.presentation?.id
    const userId = rootState?.currentUser?.id
    const userCity = rootState?.currentUser?.city

    // Use current presentation ID if available, otherwise use last known one
    // This fallback ensures the AI chatbot maintains context even when the presentation
    // state is temporarily unavailable (e.g., during navigation or state updates)
    const presentationId = currentPresentationId || state.lastKnownPresentationId
    const params = {
      presentationId, userId, userCity, assistantSource: 'slides',
    }
    Object.keys(params).forEach((key) => params[key] == null && delete params[key])
    // AHA-42634: gated migration to slides_agent_v5. The chatbot-app exposes a
    // dedicated `/slides-v5/` route that targets the new graph; users without
    // the flag stay on the v4 root entry. v5 surfaces legacy v4 threads on its
    // page so conversation history is not lost on flag flip.
    const path = rootGetters?.['aiChatbot/canUseSlidesAgentV5'] ? '/slides-v5/' : '/'
    return `${configs.aiAppUrl}${path}?${new URLSearchParams(params).toString()}`
  },

  // The native Slide-agent iframe URL, resolved WITHOUT the bridge gate; the in-editor
  // AI tab the FAB assistant renders alongside the docked panel (bridge-ON path only).
  slideAgentIframeUrl: (state, __, rootState, rootGetters) => {
    const presentationId = rootState?.presentation?.id || state.lastKnownPresentationId
    return buildSlideAgentUrl({
      presentationId,
      userId: rootState?.currentUser?.id,
      userCity: rootState?.currentUser?.city,
      useV5: rootGetters?.['aiChatbot/canUseSlidesAgentV5'],
      lang: rootState?.presenterLocale,
    })
  },
  reportPresentationAgentURL: (state, __, rootState) => {
    if (!configs.aiAppUrl) { return null }
    const currentPresentationId = rootState?.presentation?.id
    const userId = rootState?.currentUser?.id
    const userCity = rootState?.currentUser?.city
    const userName = rootState?.currentUser?.name
    // AHA-47321: the chatbot-app has no session of its own to read a user from,
    // so it builds its Unleash context out of these params. Several flags are
    // targeted by email — AHA-47321-excel-pg, which routes the report agent's
    // warehouse read to Postgres, among them — and without this they evaluate
    // as off inside the iframe while being on for the same user out here.
    const userEmail = rootState?.currentUser?.email
    const presentationId = currentPresentationId || state.lastKnownPresentationId
    const params = {
      userId, userCity, userName, userEmail, presentationId, lang: rootState?.presenterLocale,
    }
    Object.keys(params).forEach((key) => params[key] == null && delete params[key])
    return `${configs.aiAppUrl}/report-presentation/?${new URLSearchParams(params).toString()}`
  },
  masterReportAgentURL: () => {
    if (!configs.aiAppUrl) { return null }
    const params = { }
    Object.keys(params).forEach((key) => params[key] == null && delete params[key])
    return `${configs.aiAppUrl}/report/?${new URLSearchParams(params).toString()}`
  },

  isAIChatbotIndicatorVisible: (state) => state.isAIChatbotIndicatorVisible,

  isIframeReady: (state) => state.isIframeReady,

  // AHA2-146: readiness of the native Slide-agent iframe (see state comment).
  isSlideAgentIframeReady: (state) => state.isSlideAgentIframeReady,

  isGlobalPanelOpen: (state) => state.isGlobalPanelOpen,

  embedContentChange: (state) => state.embedContentChange,

  pendingHostRequest: (state) => state.pendingHostRequest,

  hostRequestSettledAt: (state) => state.hostRequestSettledAt,

  canOffAiBotMessagesInPresentationDetail: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-AHA-36909-off-ai-bot-messages')
  },
}

const mutations = {
  SET_LAST_KNOWN_PRESENTATION_ID(state, presentationId) {
    state.lastKnownPresentationId = presentationId
  },

  CLEAR_LAST_KNOWN_PRESENTATION_ID(state) {
    state.lastKnownPresentationId = null
  },

  SET_IS_AI_CHATBOT_INDICATOR_VISIBLE(state, value) {
    state.isAIChatbotIndicatorVisible = value
  },

  SET_IS_IFRAME_READY(state, value) {
    state.isIframeReady = value
  },

  SET_IS_SLIDE_AGENT_IFRAME_READY(state, value) {
    state.isSlideAgentIframeReady = value
  },

  SET_GLOBAL_PANEL_OPEN(state, value) {
    state.isGlobalPanelOpen = value
    // Single choke point for persistence — every action commits through here.
    writeGlobalPanelOpen(value)
  },

  SET_EMBED_CONTENT_CHANGE(state, value) {
    state.embedContentChange = value
  },

  // No storage write here, on purpose (see the state comment).
  SET_PENDING_HOST_REQUEST(state, value) {
    state.pendingHostRequest = value
  },

  SET_HOST_REQUEST_SETTLED_AT(state, value) {
    state.hostRequestSettledAt = value
  },
}

const actions = {
  setLastKnownPresentationId({ commit }, presentationId) {
    commit('SET_LAST_KNOWN_PRESENTATION_ID', presentationId)
  },

  clearLastKnownPresentationId({ commit }) {
    commit('CLEAR_LAST_KNOWN_PRESENTATION_ID')
  },

  updateLastKnownPresentationId({ commit }, currentPresentationId) {
    if (currentPresentationId) {
      commit('SET_LAST_KNOWN_PRESENTATION_ID', currentPresentationId)
    }
  },

  checkAIChatbotIndicatorVisibilityAction({ commit, rootState }) {
    const key = aiChatbotKeyFlag(rootState)
    if (!key) return

    if (!localStorage.getItem(key)) {
      commit('SET_IS_AI_CHATBOT_INDICATOR_VISIBLE', true)
    }
  },

  markAIChatbotIndicatorSeenAction({ commit, rootState, rootGetters }) {
    if (!rootGetters['aiChatbot/isAIChatbotIndicatorVisible']) return
    const key = aiChatbotKeyFlag(rootState)
    if (!key) return

    try {
      localStorage.setItem(key, 'true')
      commit('SET_IS_AI_CHATBOT_INDICATOR_VISIBLE', false)
    } catch (error) {
      Sentry.captureException(error)
    }
  },

  setIsIframeReadyAction({ commit }, value) {
    commit('SET_IS_IFRAME_READY', value)
  },

  // AHA2-146: readiness of the native Slide-agent iframe (AIAutoPanel), used by the
  // import postMessage gate when the tool-bridge flag is ON.
  setIsSlideAgentIframeReadyAction({ commit }, value) {
    commit('SET_IS_SLIDE_AGENT_IFRAME_READY', value)
  },

  // Open/collapse/toggle the app-wide docked AI panel. Deliberately BLIND to the
  // editor's right-nav: the assistant lives entirely as its own sidebar, so
  // opening/closing it is purely its own state — do not reintroduce cross-talk.
  openGlobalPanelAction({ commit }) {
    commit('SET_GLOBAL_PANEL_OPEN', true)
  },

  closeGlobalPanelAction({ commit }) {
    commit('SET_GLOBAL_PANEL_OPEN', false)
  },

  toggleGlobalPanelAction({ dispatch, state }) {
    if (state.isGlobalPanelOpen) dispatch('closeGlobalPanelAction')
    else dispatch('openGlobalPanelAction')
  },

  notifyEmbedContentChangedAction({ commit, state }, { kind, id }) {
    const seq = ((state.embedContentChange && state.embedContentChange.seq) || 0) + 1
    commit('SET_EMBED_CONTENT_CHANGE', { kind, id, seq })
  },

  // AHA-47691 convert hand-off. Queue a request for the master agent panel. The caller
  // (EmbedAppContainer) has already validated both ids and opened the panel;
  // `openedPanel` says whether that open is this request's doing.
  //
  // Returns a promise for the OUTCOME: `true` when the panel acked the request, `false`
  // when it was dropped unacked (expiry, panel closed, route left, bridge torn down,
  // asker gone, replaced). It always settles, exactly once, and never rejects — the
  // expiry below guarantees that even with nobody else watching the request.
  queueHostRequestAction({ commit, state, dispatch }, {
    presentationId, courseId, openedPanel = false, id = createHostRequestId(),
  }) {
    // At most ONE pending. The embed is refused while one is pending, so this only
    // covers another caller: the older request loses, and the newer one inherits its
    // claim on the panel instead of having the panel closed under it.
    const replaced = state.pendingHostRequest
    commit('SET_PENDING_HOST_REQUEST', {
      id,
      request: buildHostRequest({ presentationId, courseId }),
      queuedAt: Date.now(),
      openedPanel: Boolean(openedPanel || (replaced && replaced.openedPanel)),
    })
    if (replaced) finishHostRequest(replaced.id, false)
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        dispatch('settleHostRequestAction', { id, acked: false })
      }, HOST_REQUEST_TTL_MS)
      hostRequestRuntime.set(id, { resolve, timer })
    })
  },

  // The pending request is over: `acked` true = the panel took it, false = dropped.
  // Only when `id` is still THAT request, so a late ack or timer for one that already
  // settled (or was replaced) does nothing. A dropped request is cleared for good and
  // never delivered later; if it was what opened the panel, the panel is closed again
  // so the user is not left looking at an assistant that is not going to act.
  settleHostRequestAction({ commit, state, dispatch }, { id, acked = false } = {}) {
    const pending = state.pendingHostRequest
    if (!pending || pending.id !== id) return
    commit('SET_PENDING_HOST_REQUEST', null)
    commit('SET_HOST_REQUEST_SETTLED_AT', Date.now())
    if (acked !== true && pending.openedPanel) dispatch('closeGlobalPanelAction')
    finishHostRequest(id, acked === true)
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  mutations,
  actions,
}
